/**
 * Shared constants & helpers for the signal lifecycle:
 * active → resolved | snoozed | rejected (snoozed ← unsnooze).
 *
 * Used by SignalsSection (tabs + grouping) and SignalLifecycleActions
 * (inline + bottom-bar actions). Kept here so the status vocabulary lives
 * in one place across the redesigned components.
 */

import { HiBolt, HiCheckCircle, HiMoon, HiNoSymbol } from "react-icons/hi2";

/** Canonical lifecycle statuses, in tab order. */
export const SIGNAL_STATUSES = ["active", "resolved", "snoozed", "rejected"];

export const STATUS_LABELS = {
  active: "Active",
  resolved: "Resolved",
  snoozed: "Snoozed",
  rejected: "Rejected",
};

/**
 * Tab glyphs per status. The action icon and its destination tab share a
 * symbol — Snooze/Snoozed → moon, Reject/Rejected → no-symbol,
 * Resolve/Resolved → check — so the lifecycle reads consistently.
 */
export const STATUS_ICONS = {
  active: HiBolt,
  resolved: HiCheckCircle,
  snoozed: HiMoon,
  rejected: HiNoSymbol,
};

/** Reasons offered when rejecting a signal (mirrors BE dismiss reasons). */
export const REJECT_REASONS = [
  { value: "not_relevant", label: "Not relevant" },
  { value: "wrong_account", label: "Wrong account" },
  { value: "false_alarm", label: "False alarm" },
  { value: "already_addressed", label: "Already addressed" },
  { value: "other", label: "Other" },
];

export const REJECT_REASON_LABELS = Object.fromEntries(
  REJECT_REASONS.map(({ value, label }) => [value, label]),
);

/** Snooze duration presets. */
export const SNOOZE_DURATIONS = [
  { value: "1w", label: "1 week", days: 7 },
  { value: "2w", label: "2 weeks", days: 14 },
  { value: "1m", label: "1 month", days: 30 },
  { value: "3m", label: "3 months", days: 90 },
];

/** Compute an ISO datetime `days` from now. */
export const snoozeUntilISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
};

/**
 * Format a lifecycle actor for display.
 * Accepts either a display-name string (BE `*_by_name` fields) or a user object
 * (optimistic updates). Falls back to "a teammate".
 */
export const formatActor = (actor) => {
  if (!actor) return "a teammate";
  if (typeof actor === "string") return actor.trim() || "a teammate";
  const fullName = `${actor.first_name ?? ""} ${actor.last_name ?? ""}`.trim();
  if (fullName) return fullName;
  if (actor.email) return actor.email;
  return "a teammate";
};

/** Short relative time string from an ISO datetime. */
export const formatRelative = (iso) => {
  if (!iso) return "";
  const seconds = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

/** Absolute short date, e.g. "Jun 20, 2026". */
export const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};
